import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Search, Loader2, Plus, Download, Barcode } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";

/** UPC-A check digit (odd positions ×3). Mirrors the FileMaker calculation. */
const checkDigit = (eleven) => {
  const ds = String(eleven).split("").map(Number);
  const total = ds.reduce((s, d, i) => s + d * (i % 2 === 0 ? 3 : 1), 0);
  return (10 - (total % 10)) % 10;
};

/** FileMaker DateCreated comes back as MM/DD/YYYY — show it as YYYY-MM-DD. */
const fmtDate = (v) => {
  const s = String(v || "").trim();
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  return m ? `${m[3]}-${m[1].padStart(2, "0")}-${m[2].padStart(2, "0")}` : (s || "—");
};

/**
 * 2026-06 · UPC 查询 — browse the UPC codes synced from FileMaker (layout
 * UPC_API), mint a new one (check digit computed per UPC-A), and drop the code
 * straight into a price row of the quote item.
 */
export default function UpcLookupDialog({ open, onClose, itemNumber, description, rows = [], onPick }) {
  const { t } = useLanguage();
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [targetRow, setTargetRow] = useState("");
  const [newItemNo, setNewItemNo] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [seq5, setSeq5] = useState("");
  const [prefix, setPrefix] = useState("725828");
  const [creating, setCreating] = useState(false);
  const [seqEdited, setSeqEdited] = useState(false);

  const reqSeq = useRef(0);

  const search = useCallback(async (term) => {
    const mySeq = ++reqSeq.current;
    setLoading(true);
    try {
      const { data } = await api.get("/upc/search", { params: { q: term, limit: 60 } });
      if (mySeq !== reqSeq.current) return;   // a newer search already answered
      setItems(data.items || []);
    } catch (e) { if (mySeq === reqSeq.current) toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { if (mySeq === reqSeq.current) setLoading(false); }
  }, []);

  useEffect(() => {
    if (!open) return;
    setQ(itemNumber || "");
    search(itemNumber || "");
    setNewItemNo(itemNumber || "");
    setNewDesc(description || "");
    const firstEmpty = rows.find((r) => !r.upc) || rows[0];
    setTargetRow(firstEmpty?.id || "");
    api.get("/upc/next").then(({ data }) => { setSeq5(data.upc5); setPrefix(data.prefix); setSeqEdited(false); }).catch(() => {});
    // Deps intentionally limited to `open` — `rows` is a fresh array on every
    // parent render, and re-running this effect wiped the user's search.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const previewUpc = useMemo(() => {
    if (!/^\d{5}$/.test(seq5)) return "";
    const eleven = `${prefix}${seq5}`;
    return `${eleven}${checkDigit(eleven)}`;
  }, [prefix, seq5]);

  const pick = (code) => {
    if (!targetRow) { toast.error(t("upc.pickRowFirst")); return; }
    onPick(targetRow, code);
    toast.success(t("upc.imported").replace("{code}", code));
  };

  const createUpc = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/upc", { item_no: newItemNo.trim(), description: newDesc.trim(), upc5: seq5, auto_seq: !seqEdited });
      toast.success(t("upc.created").replace("{code}", data.upc_final));
      setItems((prev) => [data, ...prev]);
      if (targetRow) onPick(targetRow, data.upc_final);
      const { data: nxt } = await api.get("/upc/next");
      setSeq5(nxt.upc5);
      setSeqEdited(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      // Sequence may have been taken in FileMaker meanwhile — refresh the proposal.
      try {
        const { data: nxt } = await api.get("/upc/next");
        setSeq5(nxt.upc5);
        setSeqEdited(false);
      } catch { /* FM unreachable — keep current value */ }
    }
    finally { setCreating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[900px]" data-testid="upc-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <Barcode size={16} /> {t("upc.title")}
          </DialogTitle>
        </DialogHeader>

        {/* target row + search */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[220px]">
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("upc.importInto")}</Label>
            <select
              value={targetRow}
              onChange={(e) => setTargetRow(e.target.value)}
              className="mt-1 h-9 w-full rounded-md border border-border bg-background px-2 text-sm"
              data-testid="upc-target-row"
            >
              {rows.length === 0 && <option value="">{t("upc.noRows")}</option>}
              {rows.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.size || t("upc.unnamedSize")}{r.upc ? ` · ${r.upc}` : ""}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[240px]">
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("upc.search")}</Label>
            <div className="flex gap-2 mt-1">
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") search(q); }}
                placeholder={t("upc.searchPlaceholder")} className="h-9 text-sm" data-testid="upc-search-input" />
              <Button size="sm" className="h-9" onClick={() => search(q)} data-testid="upc-search-btn">
                {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
              </Button>
            </div>
          </div>
        </div>

        {/* results */}
        <div className="border border-border rounded-md max-h-[320px] overflow-y-auto">
          <table className="w-full text-xs" data-testid="upc-table">
            <thead className="bg-secondary/60 sticky top-0">
              <tr className="text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="px-2 py-1.5 text-left">{t("upc.itemNo")}</th>
                <th className="px-2 py-1.5 text-left">{t("upc.description")}</th>
                <th className="px-2 py-1.5 text-left">{t("upc.code")}</th>
                <th className="px-2 py-1.5 text-left whitespace-nowrap">{t("upc.dateCreated")}</th>
                <th className="px-2 py-1.5 text-right">{t("common.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && !loading && (
                <tr><td colSpan={5} className="text-center text-muted-foreground py-6" data-testid="upc-empty">{t("upc.empty")}</td></tr>
              )}
              {items.map((u) => (
                <tr key={u.fm_record_id} className="border-t border-border/60" data-testid={`upc-row-${u.upc_final}`}>
                  <td className="px-2 py-1.5 font-mono font-semibold">{u.item_no}</td>
                  <td className="px-2 py-1.5 max-w-[360px] truncate" title={u.description}>{u.description}</td>
                  <td className="px-2 py-1.5 font-mono">{u.upc_final}</td>
                  <td className="px-2 py-1.5 font-mono whitespace-nowrap text-muted-foreground" data-testid={`upc-date-${u.upc_final}`}>{fmtDate(u.date_created)}</td>
                  <td className="px-2 py-1.5 text-right">
                    <Button size="sm" variant="outline" className="h-6 text-[11px]"
                      onClick={() => pick(u.upc_final)} data-testid={`upc-use-${u.upc_final}`}>
                      <Download size={11} className="mr-1" /> {t("upc.use")}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* mint a new UPC */}
        <div className="border border-dashed border-border rounded-md p-3 space-y-2">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("upc.newTitle")}</p>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
            <div>
              <Label className="text-[11px]">{t("upc.itemNo")}</Label>
              <Input value={newItemNo} onChange={(e) => setNewItemNo(e.target.value)}
                className="h-8 text-sm mt-1 font-mono" data-testid="upc-new-item" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[11px]">{t("upc.description")}</Label>
              <Input value={newDesc} onChange={(e) => setNewDesc(e.target.value)}
                className="h-8 text-sm mt-1" data-testid="upc-new-desc" />
            </div>
            <div>
              <Label className="text-[11px]">{t("upc.seq5")}</Label>
              <Input value={seq5} onChange={(e) => { setSeqEdited(true); setSeq5(e.target.value.replace(/\D/g, "").slice(0, 5)); }}
                className="h-8 text-sm mt-1 font-mono" data-testid="upc-new-seq" />
            </div>
          </div>
          <div className="flex items-center justify-between gap-3 flex-wrap pt-1">
            <div className="flex items-center gap-2 text-xs">
              <span className="text-muted-foreground">{t("upc.finalPreview")}</span>
              <Badge variant="secondary" className="font-mono text-sm px-2 py-0.5" data-testid="upc-preview">
                {previewUpc || "—"}
              </Badge>
              <span className="text-[11px] text-muted-foreground">{t("upc.checkDigitHint")}</span>
            </div>
            <Button size="sm" className="h-8" onClick={createUpc}
              disabled={creating || !newItemNo.trim() || !previewUpc} data-testid="upc-create-btn">
              {creating ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Plus size={13} className="mr-1" />}
              {t("upc.createAndImport")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
